import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisEmptyList from "../../components/controls/emptylist";
import Pagenated from "../../components/pagenated";
import DynamicIcon from "../../components/dynamicicon";
import { api, download, fmtDate, tone } from "./hctc_common";
import "./hctc.css";

// Historical Case Register (screen hist_search): list / search pre 2 October 2013 cases.
const EMPTY = {
  text: "", partyName: "", divisionId: "", verificationStatus: "", digitizationStatus: "", availabilityStatus: "",
  recordStatus: "", physicalFileNumber: "", archiveReferenceNumber: "", documentType: "", filingYearFrom: "", filingYearTo: "",
};

export default function HistoricalCases() {
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const navigate = useNavigate();
  const [lk, setLk] = useState(null);
  const [codes, setCodes] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [more, setMore] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(Constants.PAGESIZE);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const body = (x) => ({
    ...x,
    divisionId: x.divisionId ? Number(x.divisionId) : null,
    filingYearFrom: x.filingYearFrom ? Number(x.filingYearFrom) : null,
    filingYearTo: x.filingYearTo ? Number(x.filingYearTo) : null,
  });

  const load = async (x, p, s) => {
    setLoading(true);
    setErr("");
    const d = await api(token, setIsExpired, `historical/list/${p}/${s}`, { method: "POST", body: body(x) });
    setLoading(false);
    if (d.code === 0) {
      setItems(d.items || []);
      setCount(d.totalCount || 0);
    } else if (d.code !== -10) setErr(d.status || "Unable to load the register");
  };

  useEffect(() => {
    (async () => {
      const [a, l] = await Promise.all([
        api(token, setIsExpired, "historical/access"),
        api(token, setIsExpired, "historical/lookups"),
      ]);
      if (a.code === 0) setCodes(a.codes || []);
      if (l.code === 0) setLk(l);
    })();
    load(EMPTY, 0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const onSearch = () => {
    setApplied(f);
    setPageIndex(0);
    load(f, 0, pageSize);
  };
  const onReset = () => {
    setF(EMPTY);
    setApplied(EMPTY);
    setPageIndex(0);
    load(EMPTY, 0, pageSize);
  };
  const onPick = (pick) => {
    const p = pick.id - 1;
    setPageIndex(p);
    load(applied, p, pageSize);
  };
  const onSize = (k) => {
    setPageSize(Number(k));
    setPageIndex(0);
    load(applied, 0, Number(k));
  };

  const onExport = async (doctype) => {
    const ext = { excel: "xlsx", msword: "docx", pdf: "pdf" }[doctype];
    setLoading(true);
    try {
      await download(token, setIsExpired, `historical/list-export/${doctype}`, `historical-cases.${ext}`, { method: "POST", body: body(applied) });
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  const sel = (name, label, options) => (
    <div className="hctc-field">
      <label>{label}</label>
      <select name={name} value={f[name]} onChange={onChange}>
        <option value="">All</option>
        {(options || []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <div>
            <h2>Historical Case Register</h2>
            <div className="hctc-sub">Cases filed before 2 October 2013. Original case numbers are kept as the primary identifier.</div>
          </div>
        </div>
        <div className="right">
          {codes.includes("hist_import") && (
            <JisButton variant="outline" onClick={() => navigate("/historical-cases/import")}>
              Import from Excel
            </JisButton>
          )}
          {codes.includes("hist_register") && (
            <JisButton onClick={() => navigate("/historical-cases/edit")}>Register Historical Case</JisButton>
          )}
        </div>
      </div>

      <div className="hctc-card">
        <div className="hctc-grid">
          <div className="hctc-field span2">
            <label>Case number, legacy number, title or HIST reference</label>
            <input name="text" value={f.text} onChange={onChange} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="e.g. ID/1234/1998 or John Doe v. ABC" />
          </div>
          <div className="hctc-field">
            <label>Party name</label>
            <input name="partyName" value={f.partyName} onChange={onChange} onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </div>
          <div className="hctc-field">
            <label>Judicial division</label>
            <select name="divisionId" value={f.divisionId} onChange={onChange}>
              <option value="">All</option>
              {frequentData?.divisions?.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </div>
          {sel("verificationStatus", "Verification", lk?.verificationStatuses)}
          {more && (
            <>
              {sel("digitizationStatus", "Digitization", lk?.digitizationStatuses)}
              {sel("availabilityStatus", "Availability", lk?.availabilityStatuses)}
              {sel("recordStatus", "Record status", lk?.recordStatuses)}
              {sel("documentType", "Has document type", lk?.documentCategories)}
              <div className="hctc-field">
                <label>Physical file number</label>
                <input name="physicalFileNumber" value={f.physicalFileNumber} onChange={onChange} />
              </div>
              <div className="hctc-field">
                <label>Archive reference</label>
                <input name="archiveReferenceNumber" value={f.archiveReferenceNumber} onChange={onChange} />
              </div>
              <div className="hctc-field">
                <label>Filed from year</label>
                <input type="number" name="filingYearFrom" value={f.filingYearFrom} onChange={onChange} min="1900" max="2013" />
              </div>
              <div className="hctc-field">
                <label>Filed to year</label>
                <input type="number" name="filingYearTo" value={f.filingYearTo} onChange={onChange} min="1900" max="2013" />
              </div>
            </>
          )}
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={onSearch}>Search</JisButton>
          <JisButton variant="outline" onClick={onReset}>
            Reset
          </JisButton>
          <JisButton variant="plain" onClick={() => setMore(!more)}>
            {more ? "Fewer filters" : "More filters"}
          </JisButton>
        </div>
      </div>

      {err && <div className="hctc-err">{err}</div>}

      <div className="table">
        <div className="section-cases" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span>Historical Cases ({count.toLocaleString()})</span>
          {count > 0 && (
            <div style={{ display: "flex", gap: 6 }}>
              <JisButton variant="plain" onClick={() => onExport("excel")}>
                Excel
              </JisButton>
              <JisButton variant="plain" onClick={() => onExport("msword")}>
                Word
              </JisButton>
              <JisButton variant="plain" onClick={() => onExport("pdf")}>
                PDF
              </JisButton>
            </div>
          )}
        </div>
        <div className="table-wrapper" style={{ overflowX: "auto" }}>
          <table className="case-table hctc-table">
            <thead>
              <tr>
                <th>HIST Ref</th>
                <th>Original Case No.</th>
                <th>Case Title</th>
                <th>Filed</th>
                <th>Division</th>
                <th>Physical File / Archive</th>
                <th>Status</th>
                <th>Docs</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{h.historicalRef}</td>
                  <td>
                    {h.originalCaseNumber || <span className="hctc-muted">{h.legacySuitNumber || h.legacyCaseNumber || "-"}</span>}
                    {h.jisCaseNumber && <div className="hctc-muted">JIS: {h.jisCaseNumber}</div>}
                  </td>
                  <td className="wrap">{h.caseTitle}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{h.originalFilingDate ? fmtDate(h.originalFilingDate) : <span className="hctc-muted">{h.filingDateStatus || "Unknown"}</span>}</td>
                  <td>{h.division || "-"}</td>
                  <td>
                    {h.physicalFileNumber || "-"}
                    {h.archiveReferenceNumber && <div className="hctc-muted">{h.archiveReferenceNumber}</div>}
                  </td>
                  <td>
                    <span className={`hctc-pill ${tone(h.verificationStatus)}`}>{h.verificationStatus}</span>
                    <span className={`hctc-pill ${tone(h.digitizationStatus)}`}>{h.digitizationStatus}</span>
                    {h.availabilityStatus && <span className={`hctc-pill ${tone(h.availabilityStatus)}`}>{h.availabilityStatus}</span>}
                    {h.recordStatus !== "Active" && <span className={`hctc-pill ${tone(h.recordStatus)}`}>{h.recordStatus}</span>}
                    {h.isMigrated && <span className="hctc-pill good">Migrated</span>}
                  </td>
                  <td>{h.documents}</td>
                  <td>
                    <JisButton variant="plain" onClick={() => navigate(`/historical-cases/${h.id}`)}>
                      <DynamicIcon iname="eye" /> View
                    </JisButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && items.length === 0 && <JisEmptyList title="No historical cases found" />}
        </div>
        {count > 0 && <Pagenated onChangePageSize={onSize} size={pageSize} total={count} maxPage={20} onPick={onPick} />}
      </div>
      <BackLoader open={loading} />
    </div>
  );
}
